import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { CpuChipIcon } from "@heroicons/react/24/outline";
import axios from "../../../api/axios";
import CustomerSystemDialog from "../../dashboard/customers/systems/system-dialog";
import { PMEntityPicker } from "./PMEntityPicker";
import { IconAvatar, PICKER_PAGE_SIZE, PickerTag, joinParts, rowsFrom } from "./pickerParts";

const systemName = (row) => row?.product?.displayTitle || row?.displayTitle || row?.system_name || `#${row?.id}`;

export const systemToItem = (row) =>
  row?.id == null
    ? null
    : {
        id: row.id,
        primary: systemName(row),
        secondary: joinParts(row.placement, row.delivery_address?.name ?? row.delivery_address?.address),
        leading: <IconAvatar icon={CpuChipIcon} />,
        tag: <PickerTag>{row.serial_number}</PickerTag>,
        object: row,
      };

export async function fetchSystemOptions({ query, customerId }) {
  const response = await axios.get(`/customers/${customerId}/systems/`, {
    params: {
      query: query || undefined,
      page: 1,
      per_page: PICKER_PAGE_SIZE,
      sortBy: "created_at",
      sort: "desc",
    },
  });
  return rowsFrom(response).map(systemToItem);
}

/**
 * Facility (anlegg) picker (new design). Lists the customer's facilities with
 * serial number and placement; "New facility" opens the customer facility
 * dialog in create mode. Emits `onChange({ target: { name, value, data } })`
 * and, for legacy SystemSelector callers, `onSelectedItemChange(row)`.
 */
export function SystemPicker({
  name = "system_id",
  label,
  value,
  customerId,
  onChange,
  onSelectedItemChange,
  setFieldValue,
  disabled = false,
  error,
  allowCreate = true,
}) {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const selectedId = value?.id ?? null;
  const hasCustomer = customerId != null && customerId !== "";
  const canOpenDetail = hasCustomer && selectedId != null;

  const loadOptions = useCallback(
    ({ query }) => fetchSystemOptions({ query, customerId }),
    [customerId]
  );

  const emit = (row) => {
    const id = row?.id ?? null;
    setFieldValue?.(name, id);
    onSelectedItemChange?.(row ?? null);
    onChange?.({ target: { name, value: id, data: row ?? null } });
  };

  return (
    <>
      <PMEntityPicker
        label={label ?? t("reportWizard.system")}
        labelAction={canOpenDetail ? { action: () => setDetailOpen(true) } : null}
        value={systemToItem(value)}
        placeholder={t(hasCustomer ? "pickers.system.placeholder" : "pickers.system.needsCustomer")}
        searchPlaceholder={t("pickers.system.search")}
        disabled={disabled || !hasCustomer}
        error={error}
        loadOptions={loadOptions}
        pageSize={PICKER_PAGE_SIZE}
        onSelect={(item) => emit(item?.object ?? null)}
        emptyText={t("pickers.system.empty")}
        footerAction={allowCreate && hasCustomer ? { label: t("pickers.system.create"), onClick: () => setCreateOpen(true) } : undefined}
      />
      {createOpen && (
        <CustomerSystemDialog
          open
          customer={{ id: customerId }}
          allowLinkActions={false}
          context="customer"
          onClose={() => setCreateOpen(false)}
          onRefresh={(row) => {
            setCreateOpen(false);
            if (row?.id != null) emit(row);
          }}
        />
      )}
      {detailOpen && canOpenDetail && (
        <CustomerSystemDialog
          open
          onClose={() => setDetailOpen(false)}
          customer={{ id: customerId }}
          selectedSystem={selectedId}
          allowLinkActions={false}
          context="customer"
          onRefresh={(row) => {
            if (row?.id != null) emit(row);
          }}
        />
      )}
    </>
  );
}
